import { useState } from 'react';

/**
 * A tab's local working copy of saved settings, shared by the Settings and Discount Limits tabs:
 * edits stay in the draft until Save, a refetch with newer settings (saved elsewhere) replaces
 * the draft only while it is unedited, and a save resets only a draft left untouched while it
 * was in flight.
 */

export interface SavedDraftOptions<Saved, Draft> {
	toDraft: (saved: Saved) => Draft;
	/** True when the draft would save exactly what `saved` holds. */
	isUnchanged: (draft: Draft, saved: Saved) => boolean;
	/** True when two saved values are the same, so a refetch that echoes them changes nothing. */
	sameSaved: (a: Saved, b: Saved) => boolean;
}

export interface SavedDraft<Saved, Draft> {
	draft: Draft;
	isDirty: boolean;
	update: (patch: Partial<Draft>) => void;
	/** After `sent` was saved as `saved`: the new baseline, and a fresh draft unless edited since. */
	settle: (sent: Draft, saved: Saved) => void;
}

export function useSavedDraft<Saved, Draft extends object>(
	saved: Saved,
	{ toDraft, isUnchanged, sameSaved }: SavedDraftOptions<Saved, Draft>
): SavedDraft<Saved, Draft> {
	// What the server holds, as last seen: the draft is dirty when it differs.
	const [baseline, setBaseline] = useState(saved);
	const [draft, setDraft] = useState(() => toDraft(saved));
	const isDirty = !isUnchanged(draft, baseline);

	// Adjusted during render rather than in an effect, so the stale draft never paints.
	const [synced, setSynced] = useState(saved);
	if (synced !== saved) {
		setSynced(saved);
		if (!sameSaved(saved, baseline)) {
			setBaseline(saved);
			if (!isDirty) setDraft(toDraft(saved));
		}
	}

	const update = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

	const settle = (sent: Draft, next: Saved) => {
		setBaseline(next);
		setDraft((current) => (current === sent ? toDraft(next) : current));
	};

	return { draft, isDirty, update, settle };
}
